/**
 * Mapping Rules Management Modal
 *
 * Modal for managing calendar mapping rules that auto-copy post variations
 * from source calendar to target calendars.
 *
 * Features:
 * - View existing rules
 * - Add new rules (variation title + target calendar)
 * - Delete individual rules
 * - Batch save all rules
 * - Apply rules retroactively to existing posts
 */

import { useState, useMemo } from 'react';
import { endOfDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { Icon } from '@/components/ui/Icon';
import { Toggle } from '@/components/ui/Toggle';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { useCalendars } from '@/hooks/useCalendar';
import {
  useMappingRulesByCalendar,
  useVariationTitles,
  useSaveRulesForCalendar,
  useDeleteMappingRule,
  useApplyRulesRetroactive,
  useUpdateMappingRule,
} from '@/hooks/useMappingRules';
import { toast } from '@/utils/toast';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';

interface MappingRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  calendarId: number;
  calendarName: string;
}

interface NewRule {
  targetCalendarId: number;
  variationTitle: string;
}

export const MappingRulesModal = ({
  isOpen,
  onClose,
  calendarId,
  calendarName,
}: MappingRulesModalProps) => {
  const { t } = useTranslation('common');

  // Fetch data
  const { data: existingRules, isLoading: rulesLoading } = useMappingRulesByCalendar(calendarId);
  const { data: calendars } = useCalendars({ isActive: true });
  const { data: variationTitles } = useVariationTitles();

  // Mutations
  const saveRulesMutation = useSaveRulesForCalendar();
  const deleteRuleMutation = useDeleteMappingRule();
  const applyRetroactiveMutation = useApplyRulesRetroactive();
  const updateRuleMutation = useUpdateMappingRule();

  // Local state for new rules being added
  const [newRules, setNewRules] = useState<NewRule[]>([]);
  const [selectedVariation, setSelectedVariation] = useState<string>('');
  const [selectedTargetCalendar, setSelectedTargetCalendar] = useState<string>('');

  // State for apply retroactively
  const [dateRange, setDateRange] = useState<{ from: Date | null; to: Date | null }>({
    from: null,
    to: null,
  });

  // Unsaved-work guard: dirty while open and there are pending (unsaved)
  // new rules or an in-progress "add rule" selection.
  useUnsavedGuard(
    isOpen && (newRules.length > 0 || selectedVariation !== '' || selectedTargetCalendar !== ''),
    'mapping-rules-modal'
  );

  // Filter out current calendar from target options
  const targetCalendarOptions = useMemo(() => {
    return (
      calendars
        ?.filter((cal) => cal.id !== calendarId)
        .map((cal) => ({
          value: cal.id.toString(),
          label: cal.name,
        })) || []
    );
  }, [calendars, calendarId]);

  // Variation title options
  const variationOptions = useMemo(() => {
    return (
      variationTitles?.map((title) => ({
        value: title,
        label: title,
      })) || []
    );
  }, [variationTitles]);

  // Combine existing rules and new rules for display
  const allRules = useMemo(() => {
    const existing =
      existingRules?.map((rule) => ({
        id: rule.id,
        targetCalendarId: rule.targetCalendarId,
        variationTitle: rule.variationTitle,
        targetCalendarName: rule.targetCalendar?.name || 'Unknown',
        isActive: rule.isActive,
        isNew: false,
      })) || [];

    const newOnes = newRules.map((rule, index) => ({
      id: -index - 1, // Temporary negative ID for new rules
      targetCalendarId: rule.targetCalendarId,
      variationTitle: rule.variationTitle,
      targetCalendarName:
        calendars?.find((cal) => cal.id === rule.targetCalendarId)?.name || 'Unknown',
      isActive: true, // New rules are active by default
      isNew: true,
    }));

    return [...existing, ...newOnes];
  }, [existingRules, newRules, calendars]);

  /**
   * Add new rule to local state
   */
  const handleAddRule = () => {
    if (!selectedVariation || !selectedTargetCalendar) {
      toast.error(t('mapping_select_both_required'));
      return;
    }

    const targetCalendarId = parseInt(selectedTargetCalendar);

    // Check for duplicates
    const duplicate = allRules.find(
      (rule) =>
        rule.targetCalendarId === targetCalendarId && rule.variationTitle === selectedVariation
    );

    if (duplicate) {
      toast.error(t('mapping_rule_exists'));
      return;
    }

    setNewRules([...newRules, { targetCalendarId, variationTitle: selectedVariation }]);
    setSelectedVariation('');
    setSelectedTargetCalendar('');
    toast.success(t('mapping_rule_added'));
  };

  /**
   * Delete existing rule
   */
  const handleDeleteRule = async (ruleId: number, isNew: boolean) => {
    if (isNew) {
      // Remove from local state
      setNewRules(newRules.filter((_, index) => -index - 1 !== ruleId));
      toast.success(t('mapping_rule_removed'));
    } else {
      // Delete from backend
      try {
        await deleteRuleMutation.mutateAsync(ruleId);
        toast.success(t('mapping_rule_deleted'));
      } catch (error) {
        toast.error(t('mapping_delete_failed'));
      }
    }
  };

  /**
   * Toggle rule active status
   */
  const handleToggleRule = async (ruleId: number, currentlyActive: boolean) => {
    try {
      await updateRuleMutation.mutateAsync({
        id: ruleId,
        data: { isActive: !currentlyActive },
      });
      toast.success(currentlyActive ? t('mapping_rule_disabled') : t('mapping_rule_enabled'));
    } catch (error) {
      toast.error(t('mapping_toggle_failed'));
    }
  };

  /**
   * Save all rules (batch save)
   */
  const handleSaveRules = async () => {
    try {
      const rulesToSave = allRules.map((rule) => ({
        targetCalendarId: rule.targetCalendarId,
        variationTitle: rule.variationTitle,
      }));

      await saveRulesMutation.mutateAsync({
        sourceCalendarId: calendarId,
        rules: rulesToSave,
      });

      toast.success(t('mapping_rules_saved'));
      setNewRules([]); // Clear new rules
      onClose();
    } catch (error) {
      toast.error(t('mapping_rules_save_failed'));
    }
  };

  /**
   * Apply rules retroactively
   */
  const handleApplyRetroactive = async () => {
    if (!dateRange.from || !dateRange.to) {
      toast.error(t('mapping_select_date_range'));
      return;
    }

    try {
      const result = await applyRetroactiveMutation.mutateAsync({
        sourceCalendarId: calendarId,
        dateFrom: dateRange.from,
        dateTo: dateRange.to ? endOfDay(dateRange.to) : dateRange.to,
      });

      // Show detailed result: posts processed and mappings created
      if (result.mappingsCreated > 0) {
        toast.success(t('mapping_rules_applied_detail', {
          posts: result.postsProcessed,
          mappings: result.mappingsCreated
        }));
      } else if (result.postsProcessed > 0) {
        toast.success(t('mapping_rules_no_new_mappings', { posts: result.postsProcessed }));
      } else {
        toast.success(t('mapping_rules_no_posts_found'));
      }

      // Show errors if any
      if (result.errors && result.errors.length > 0) {
        toast.error(t('mapping_rules_some_errors', { count: result.errors.length }));
      }

      setDateRange({ from: null, to: null });
    } catch (error) {
      toast.error(t('mapping_rules_apply_failed'));
    }
  };

  /**
   * Cancel and close modal
   */
  const handleCancel = () => {
    setNewRules([]);
    setSelectedVariation('');
    setSelectedTargetCalendar('');
    setDateRange({ from: null, to: null });
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleCancel()}
      title={`${t('mapping_rules_for')} "${calendarName}"`}
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-1.5 sm:gap-2">
          <CancelButton onClick={handleCancel} tooltip="Cancel" />
          <Button
            variant="primary"
            onClick={handleSaveRules}
            loading={saveRulesMutation.isPending}
            disabled={allRules.length === 0}
          >
            {t('mapping_save_rules')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Description */}
        <p className="text-sm text-gray-600">
          {t('mapping_rules_description')}
        </p>

        {/* Existing Rules */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900">{t('mapping_existing_rules')}</h3>

          {rulesLoading ? (
            <div className="text-sm text-gray-500 py-4">{t('mapping_loading_rules')}</div>
          ) : allRules.length === 0 ? (
            <div className="text-sm text-gray-500 py-4 border border-gray-200 rounded-lg px-4 bg-gray-50">
              {t('mapping_no_rules_yet')}
            </div>
          ) : (
            <div className="space-y-2">
              {allRules.map((rule) => (
                <div
                  key={rule.id}
                  className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                    rule.isActive
                      ? 'border-gray-200 bg-white hover:bg-gray-50'
                      : 'border-gray-100 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Icon
                      name={ICONS.LANGUAGE}
                      size="sm"
                      color={rule.isActive ? 'text-gray-400' : 'text-gray-300'}
                      ariaHidden
                    />
                    <div className="flex-1">
                      <span className={`text-sm font-medium ${rule.isActive ? 'text-gray-900' : 'text-gray-500'}`}>
                        "{rule.variationTitle}"
                      </span>
                      <span className="text-sm text-gray-500 mx-2">→</span>
                      <span className={`text-sm ${rule.isActive ? 'text-gray-700' : 'text-gray-400'}`}>
                        {rule.targetCalendarName}
                      </span>
                    </div>
                    {rule.isNew && (
                      <span className="text-xs bg-primary-50 text-primary-800 px-2 py-0.5 rounded-full">
                        {t('mapping_new_badge')}
                      </span>
                    )}
                    {!rule.isNew && !rule.isActive && (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        {t('mapping_disabled_badge')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Toggle active status - only for existing rules */}
                    {!rule.isNew && (
                      <Toggle
                        ariaLabel={`"${rule.variationTitle}" → ${rule.targetCalendarName}`}
                        checked={rule.isActive}
                        onChange={() => handleToggleRule(rule.id, rule.isActive)}
                      />
                    )}
                    <Tooltip content={t('mapping_delete_rule', { defaultValue: 'Delete rule' })}>
                      <button
                        type="button"
                        onClick={() => handleDeleteRule(rule.id, rule.isNew)}
                        disabled={deleteRuleMutation.isPending}
                        className="w-8 h-8 flex items-center justify-center rounded-md transition-colors disabled:opacity-50"
                      >
                        <Icon name={ICONS.TRASH} size="sm" className="text-gray-400 hover:text-bs-danger transition-colors" ariaHidden />
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add New Rule */}
        <div className="space-y-3 border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-900">{t('mapping_add_new_rule')}</h3>

          <div className="grid grid-cols-2 gap-3">
            <Select
              label={t('mapping_variation_label')}
              placeholder={t('mapping_select_variation')}
              options={variationOptions}
              value={selectedVariation}
              onValueChange={setSelectedVariation}
            />

            <Select
              label={t('mapping_target_calendar_label')}
              placeholder={t('mapping_select_calendar')}
              options={targetCalendarOptions}
              value={selectedTargetCalendar}
              onValueChange={setSelectedTargetCalendar}
            />
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddRule}
              disabled={!selectedVariation || !selectedTargetCalendar}
              className="flex items-center gap-2"
            >
              <Icon name={ICONS.PLUS} size="sm" ariaHidden />
              {t('mapping_add_rule')}
            </Button>
          </div>
        </div>

        {/* Apply Retroactively */}
        <div className="space-y-3 border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-900">{t('mapping_apply_retroactive')}</h3>
          <p className="text-xs text-gray-600">
            {t('mapping_retroactive_description')}
          </p>

          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            placeholder={t('select_date_range')}
            openUpward
          />

          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleApplyRetroactive}
              disabled={!dateRange.from || !dateRange.to}
              loading={applyRetroactiveMutation.isPending}
              className="flex items-center gap-2"
            >
              <Icon name={ICONS.MAGIC_WAND} size="sm" ariaHidden />
              {t('mapping_apply_rules')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
